import { interpolate, Easing, InterpolateOptions } from 'remotion';
import { AudioElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/AudioElement';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export function getAudioDuration(element: AudioElement, fps: number): number {
  const totalFrames = element.toFrame - element.fromFrame;
  return totalFrames / fps;
}

/**
 * Convert fade percentage to seconds
 * @param percentage - Fade percentage (0-100)
 * @param audioDurationSeconds - Total audio duration in seconds
 * @returns Fade duration in seconds (max is audioDuration/2)
 */
export function fadePercentageToSeconds(percentage: number, audioDurationSeconds: number): number {
  const maxFadeDurationSeconds = audioDurationSeconds / 2;
  return (percentage / 100) * maxFadeDurationSeconds;
}

/**
 * Calculate volume for a given frame with fade in/out applied
 * Uses Remotion's interpolate function to create smooth fade curves
 *
 * For looping audio, fades are applied based on timeline position, not audio loop position.
 * This ensures fade in only occurs at the start of the timeline and fade out only at the end,
 * without fading during each audio loop.
 *
 * @param frame - Timeline frame number (local to audio element, starts at 0 when element begins)
 * @param element - Audio element with fade properties (fadeIn/fadeOut as percentages 0-100)
 * @param fps - Frames per second
 * @returns Volume value between 0 and 1
 */
export function calculateVolumeForFrame(frame: number, element: AudioElement, fps: number): number {
  const { details, fromFrame, toFrame } = element;

  // Get base volume from the first volume segment
  const baseVolume = details.volume[0]?.value / 100 || 0;

  const featureFlags = featureFlagStore.getSnapshot();
  if (!featureFlags[FeatureFlagKeys.Use_CL_Audio_Transitions]) {
    return baseVolume;
  }

  const audioDurationSeconds = (toFrame - fromFrame) / fps;
  const maxFadeDurationSeconds = audioDurationSeconds / 2;

  const fadeInRaw = details.fadeIn ?? 0;
  const fadeOutRaw = details.fadeOut ?? 0;

  const fadeIn =
    fadeInRaw > 100
      ? Math.min(fadeInRaw, maxFadeDurationSeconds)
      : fadePercentageToSeconds(fadeInRaw, audioDurationSeconds);
  const fadeOut =
    fadeOutRaw > 100
      ? Math.min(fadeOutRaw, maxFadeDurationSeconds)
      : fadePercentageToSeconds(fadeOutRaw, audioDurationSeconds);

  // If no fades are set, return base volume
  if (fadeIn === 0 && fadeOut === 0) {
    return baseVolume;
  }

  // Frame parameter is already local (starts at 0 when audio begins)
  const localFrame = frame;
  const totalFrames = toFrame - fromFrame;

  // Convert fade durations from seconds to frames
  const fadeInFrames = fadeIn * fps;
  const fadeOutFrames = fadeOut * fps;

  // Validate fade durations don't exceed available frames
  const validatedFadeInFrames = Math.min(fadeInFrames, totalFrames);
  const validatedFadeOutFrames = Math.min(fadeOutFrames, totalFrames);

  // Ensure fades don't overlap
  const effectiveFadeInFrames = Math.min(validatedFadeInFrames, totalFrames - validatedFadeOutFrames);
  const effectiveFadeOutFrames = Math.min(validatedFadeOutFrames, totalFrames - effectiveFadeInFrames);

  let fadeMultiplier = 1;

  if (effectiveFadeInFrames > 0 && localFrame < effectiveFadeInFrames) {
    const inputRange = [0, effectiveFadeInFrames];
    const outputRange = [0, 1];
    const interpolateConfig: InterpolateOptions = {
      easing: Easing.in(Easing.exp),
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    };

    fadeMultiplier = interpolate(localFrame, inputRange, outputRange, interpolateConfig);
  } else if (effectiveFadeOutFrames > 0 && localFrame > totalFrames - effectiveFadeOutFrames) {
    const fadeOutStartFrame = totalFrames - effectiveFadeOutFrames;
    const inputRange = [fadeOutStartFrame, totalFrames];
    const outputRange = [1, 0];
    const interpolateConfig: InterpolateOptions = {
      easing: Easing.out(Easing.exp),
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    };

    fadeMultiplier = interpolate(localFrame, inputRange, outputRange, interpolateConfig);
  }

  return baseVolume * fadeMultiplier;
}
